import { create } from 'zustand';
import { MessagingConversation } from '@/types/shared/messaging';
import { mockMessagingConversations } from '@/data/entities/residents/mockMessagingData';

interface MessagingState {
  conversations: MessagingConversation[];
  setConversations: (updater: (prev: MessagingConversation[]) => MessagingConversation[]) => void;
  upsertConversation: (conversation: MessagingConversation) => void;
  markConversationAsRead: (conversationId: string) => void;
  markAllAsRead: () => void;
  removeConversation: (conversationId: string) => void;
  togglePinConversation: (conversationId: string) => void;
  toggleArchiveConversation: (conversationId: string) => void;
}

export const useMessagingStore = create<MessagingState>((set) => ({
  conversations: mockMessagingConversations,
  setConversations: (updater) =>
    set((state) => ({
      conversations: updater(state.conversations),
    })),
  upsertConversation: (conversation) =>
    set((state) => {
      const exists = state.conversations.find((c) => c.id === conversation.id);
      if (exists) {
        return {
          conversations: state.conversations.map((c) =>
            c.id === conversation.id ? { ...exists, ...conversation } : c,
          ),
        };
      }

      return {
        conversations: [conversation, ...state.conversations],
      };
    }),
  removeConversation: (conversationId) =>
    set((state) => ({
      conversations: state.conversations.filter(
        (conversation) => conversation.id !== conversationId,
      ),
    })),
  markConversationAsRead: (conversationId) =>
    set((state) => ({
      conversations: state.conversations.map((conversation) =>
        conversation.id === conversationId
          ? {
              ...conversation,
              unreadCount: 0,
              lastMessage: conversation.lastMessage
                ? { ...conversation.lastMessage, isRead: true }
                : conversation.lastMessage,
            }
          : conversation,
      ),
    })),
  markAllAsRead: () =>
    set((state) => ({
      conversations: state.conversations.map((conversation) => ({
        ...conversation,
        unreadCount: 0,
        lastMessage: conversation.lastMessage
          ? { ...conversation.lastMessage, isRead: true }
          : conversation.lastMessage,
      })),
    })),
  togglePinConversation: (conversationId) =>
    set((state) => ({
      conversations: state.conversations.map((conversation) =>
        conversation.id === conversationId
          ? { ...conversation, isPinned: !conversation.isPinned }
          : conversation,
      ),
    })),
  toggleArchiveConversation: (conversationId) =>
    set((state) => ({
      conversations: state.conversations.map((conversation) =>
        conversation.id === conversationId
          ? { ...conversation, isArchived: !conversation.isArchived }
          : conversation,
      ),
    })),
}));
